import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth.store';
import { useRestaurantStore } from '@/stores/restaurant.store';
import { useRealtimeStream } from '@/hooks/useRealtimeStream';
import { realtimeStreamPath } from '@/lib/realtime-stream-path';

/**
 * Live reservation updates via Server-Sent Events. The backend streams a
 * lightweight `invalidate` signal whenever a reservation for the selected
 * restaurant is created or changed (online booking, staff action on another
 * device, the lifecycle sweeper); we just invalidate the cache so the open
 * Reservations view refetches at once.
 *
 * This is the "instant" layer. The reservation queries also run a polling
 * backstop (see useReservations), so a dropped connection only costs a few
 * seconds of latency - never a missed booking. Connection handling - auth
 * header, token refresh on 401, reconnect - lives in useRealtimeStream.
 */
export function useReservationsRealtime() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const queryClient = useQueryClient();

  const onEvent = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['reservations'] });
  }, [queryClient]);

  useRealtimeStream({
    path: restaurantId ? realtimeStreamPath(restaurantId, 'reservations') : null,
    label: 'reservations',
    enabled: isAuthenticated,
    onEvent,
  });
}
